import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { AlertReasons, NotificationChannel, UserNotificationSettings } from '../types/projectTeam';
import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  alertReasons: AlertReasons;
}

function mergeChannels(
  existing: NotificationChannel | null | undefined,
  incoming: NotificationChannel | null | undefined
): NotificationChannel {
  return {
    email: incoming?.email ?? existing?.email ?? false,
    sms: incoming?.sms ?? existing?.sms ?? false,
  };
}

export function useUpdateCurrentUserNotificationSettings() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: Params) =>
      post({
        url: `/alerts/notifications/${params.projectId}`,
        body: params.alertReasons,
      }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.getUserNotifications(),
      });

      const previousNotifications = queryClient.getQueryData(queryKeys.getUserNotifications());

      queryClient.setQueryData(
        queryKeys.getUserNotifications(),
        (old: UserNotificationSettings[] | undefined) => {
          if (!old) return old;
          return old.map((setting) => {
            if (setting.projectId.toString() !== variables.projectId) return setting;
            return {
              ...setting,
              notifications: {
                sensorAlert: mergeChannels(
                  setting.notifications.sensorAlert,
                  variables.alertReasons.sensorAlert
                ),
                gatewayOffPower: mergeChannels(
                  setting.notifications.gatewayOffPower,
                  variables.alertReasons.gatewayOffPower
                ),
                weeklySummary: mergeChannels(
                  setting.notifications.weeklySummary,
                  variables.alertReasons.weeklySummary
                ),
              },
            };
          });
        }
      );

      return { previousNotifications };
    },
    onError: (_, __, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(queryKeys.getUserNotifications(), context.previousNotifications);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.getUserNotifications(),
      });
    },
  });

  const update = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    update,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
